import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type StandingsPointsConfig,
  type StandingsPayload,
  type StandingsRow,
  type Tournament,
  type TournamentMatch,
  type TournamentRound,
  type TournamentDivision,
  type TournamentGroup,
  type RoundRobinQualificationPreviewPayload,
} from "../lib/api";
import { groupNameWithDivision, repeatedTournamentGroupNames } from "../lib/groupLabels";
import { escapeHtml, openPrintWindow } from "../lib/printTemplate";
import { uploadAssetHref } from "../lib/assets";
import { scoreVocab, type ScoreVocab } from "../lib/labels";
import {
  initialRankDisplay,
  rowsHaveResults,
  showPointsColumn,
  standingsHaveResults,
} from "../lib/standingsRules";
import FieldHint from "../components/FieldHint";
import { parseGames } from "../lib/scoreParsing";
import RankMovementBadge from "../components/RankMovementBadge";
import TableScrollWrap from "../components/TableScrollWrap";
import TournamentBottomNav from "../components/TournamentBottomNav";
import {
  activeRoundRobinPhaseConfig,
  groupScheduleTypeLabel,
  isRoundRobinPlayoffPhase,
  playoffStageLabelsByRound,
  qualificationRulesSummary,
  roundRobinPhaseSummary,
} from "../lib/roundRobinPhases";
import {
  combinedRawLabelDisplay,
  matchRawLabelsBySort,
  stageForRawLabels,
} from "../lib/roundLabelAliases";

interface RankingStats {
  w: number;
  l: number;
  gg: number;
  gp: number;
  dif: number;
  /** Derrotas por W.O./no-show/default (solo en las stats por semana). */
  lwo?: number;
}

interface WeekOption {
  key: string;
  number: string;
  sort: number;
  /** Etiqueta canónica cuando la semana es una ronda de playoffs (Semifinal, Final…). */
  stage?: string;
  /** TODOS los raw labels del número (aliases "Semana 4"/"Jornada 4"), ordenados. */
  rawLabels?: string[];
}

/** Display de una semana/ronda: etapa canónica si es derivable (y cubre a
 * TODOS los aliases); si no, el/los round_label CRUDOS — un solo raw exacto o
 * el display combinado determinista "Jornada 4 · Semana 4" cuando el número
 * mezcla aliases — contrato display-only del gate #136. Compartido por el
 * <select>, los chips y cualquier resumen. Exportado para regresión. */
export function weekOptionDisplay(option: { key: string; stage?: string; rawLabels?: string[] }): string {
  if (option.stage) return option.stage;
  if (option.rawLabels && option.rawLabels.length > 1) {
    return combinedRawLabelDisplay(option.rawLabels);
  }
  return option.key;
}

/** Selección fail-safe: la opción cuyo key coincide O que contiene el label
 * seleccionado entre sus aliases (defaultWeekKey puede devolver cualquiera de
 * los raws del bucket). Exportado para regresión. */
export function findWeekOption(options: WeekOption[], selectedKey: string): WeekOption | undefined {
  return options.find(
    (option) => option.key === selectedKey || (option.rawLabels ?? []).includes(selectedKey),
  );
}

interface RankingDraftGroup {
  groupId: number | null;
  groupName: string;
  rows: StandingsRow[];
}

const EMPTY_STATS: RankingStats = { w: 0, l: 0, gg: 0, gp: 0, dif: 0 };

function roundNumber(label: string | null): string {
  if (!label) return "—";
  const found = label.match(/(\d+)/);
  return found ? found[1] : label;
}

function roundSortValue(label: string | null): number {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const found = label.match(/(\d+)/);
  return found ? Number(found[1]) : Number.MAX_SAFE_INTEGER - 1;
}

export function makeWeekOptions(matches: TournamentMatch[], rounds: TournamentRound[]): WeekOption[] {
  // Rondas de playoffs → etiqueta canónica de tenis (display-only; la clave
  // interna sigue siendo el round_label crudo).
  const playoffStages = playoffStageLabelsByRound(matches);
  // Raw labels REALES por número (TODOS los aliases, ordenados): un torneo
  // semanal guarda "Semana 4" aunque la ronda solo tenga número, y datos
  // legados pueden mezclar "Semana 4" y "Jornada 4" en el mismo número.
  const rawsBySort = matchRawLabelsBySort(matches);
  const map = new Map<number, WeekOption>();
  for (const round of rounds) {
    // El raw de los MATCHES gana sobre el nombre/fallback de la ronda: si los
    // partidos guardan "Semana 4", la opción no puede llegar como "Jornada 4".
    // Con aliases mixtos la clave es el primer raw ORDENADO (determinista, no
    // el primero que llegue) y el display los muestra todos.
    const raws = rawsBySort.get(round.round_number) ?? [];
    const key = raws[0] ?? roundLabelFor(round);
    map.set(round.round_number, {
      key,
      number: String(round.round_number),
      sort: round.round_number,
      stage: stageForRawLabels(raws.length > 0 ? raws : [key.trim()], playoffStages),
      rawLabels: raws.length > 0 ? raws : undefined,
    });
  }
  for (const match of matches) {
    const sort = roundSortValue(match.round_label);
    if (map.has(sort)) continue;
    const raws = rawsBySort.get(sort) ?? [];
    const key = raws[0] ?? (match.round_label || "Sin semana");
    map.set(sort, {
      key,
      number: roundNumber(raws[0] ?? match.round_label),
      sort,
      stage: stageForRawLabels(raws.length > 0 ? raws : [(match.round_label ?? "").trim()], playoffStages),
      rawLabels: raws.length > 0 ? raws : undefined,
    });
  }
  return Array.from(map.values()).sort(
    (a, b) => a.sort - b.sort || a.key.localeCompare(b.key, "es", { numeric: true }),
  );
}

function hasResult(match: TournamentMatch): boolean {
  return Boolean(
    match.winner_participant_id ||
      match.score_text ||
      ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "bye"].includes(match.status),
  );
}

function roundLabelFor(round: TournamentRound): string {
  return round.name || `Jornada ${round.round_number}`;
}

function isRoundComplete(matches: TournamentMatch[], round: TournamentRound): boolean {
  const label = roundLabelFor(round);
  const realMatches = matches.filter(
    (match) =>
      roundSortValue(match.round_label) === round.round_number &&
      (match.round_label === label || roundSortValue(match.round_label) === round.round_number) &&
      Boolean(match.player1_participant_id && match.player2_participant_id),
  );
  return realMatches.length > 0 && realMatches.every(hasResult);
}

function defaultWeekKey(matches: TournamentMatch[], rounds: TournamentRound[]): string {
  // Mismo criterio que makeWeekOptions: el raw de los MATCHES gana sobre el
  // nombre/fallback de la ronda — y con aliases mixtos, el primer raw
  // ORDENADO — para que la clave por default siempre coincida con la clave
  // de la opción (gate #136). `findWeekOption` además acepta cualquier alias.
  const rawsBySort = matchRawLabelsBySort(matches);
  const keyFor = (round: TournamentRound): string =>
    rawsBySort.get(round.round_number)?.[0] ?? roundLabelFor(round);
  const orderedRounds = [...rounds].sort((a, b) => a.round_number - b.round_number);
  const latestPublishedRound = orderedRounds
    .filter((round) => round.status === "published")
    .at(-1);
  if (latestPublishedRound) {
    const nextRound = orderedRounds.find((round) => round.round_number === latestPublishedRound.round_number + 1);
    if (nextRound && nextRound.status !== "published" && isRoundComplete(matches, latestPublishedRound)) {
      return keyFor(nextRound);
    }
    return keyFor(latestPublishedRound);
  }
  const withResults = matches
    .filter(hasResult)
    .sort((a, b) => roundSortValue(a.round_label) - roundSortValue(b.round_label));
  const latest = withResults.at(-1);
  if (latest?.round_label) return latest.round_label;
  const firstRound = orderedRounds[0];
  if (firstRound) return keyFor(firstRound);
  const first = [...matches].sort((a, b) => roundSortValue(a.round_label) - roundSortValue(b.round_label))[0];
  return first?.round_label || "Sin semana";
}

function addStats(stats: Map<number, RankingStats>, participantId: number | null, patch: Partial<RankingStats>) {
  if (!participantId) return;
  const current = stats.get(participantId) ?? { ...EMPTY_STATS };
  const next = {
    w: current.w + (patch.w ?? 0),
    l: current.l + (patch.l ?? 0),
    gg: current.gg + (patch.gg ?? 0),
    gp: current.gp + (patch.gp ?? 0),
    dif: 0,
    lwo: (current.lwo ?? 0) + (patch.lwo ?? 0),
  };
  next.dif = next.gg - next.gp;
  stats.set(participantId, next);
}

function latestLegacyStatsWeek(legacy: StandingsPayload["legacy_stats"] | undefined): string | null {
  if (!legacy?.available || !legacy.by_week) return null;
  const latest = Object.keys(legacy.by_week)
    .map((week) => Number(week))
    .filter((week) => Number.isFinite(week))
    .sort((a, b) => b - a)[0];
  return latest ? String(latest) : null;
}

const WALKOVER_LOSS_STATUSES = new Set(["walkover", "no_show", "defaulted"]);

/** Puntos de liga con las mismas W/L que muestra la fila (filtradas por
 * semana), para que la columna Puntos no se contradiga con W y L.
 * Exportado para regresión. */
export function leaguePointsFromStats(stats: RankingStats, config: StandingsPointsConfig): number {
  const walkoverLosses = Math.min(stats.lwo ?? 0, stats.l);
  return (
    stats.w * config.win +
    (stats.l - walkoverLosses) * config.loss_completed +
    walkoverLosses * config.walkover_loss
  );
}

function cumulativeStatsUntil(
  matches: TournamentMatch[],
  selectedWeekSort: number,
  sport?: string | null,
): Map<number, RankingStats> {
  const stats = new Map<number, RankingStats>();
  for (const match of matches) {
    if (roundSortValue(match.round_label) >= selectedWeekSort) continue;
    if (!hasResult(match)) continue;
    const p1 = match.player1_participant_id;
    const p2 = match.player2_participant_id;
    if (!p1 || !p2) continue;
    const sets = parseGames(match.score_text, sport);
    let p1Games = 0;
    let p2Games = 0;
    for (const [a, b] of sets) {
      p1Games += a;
      p2Games += b;
    }
    addStats(stats, p1, { gg: p1Games, gp: p2Games });
    addStats(stats, p2, { gg: p2Games, gp: p1Games });
    // Igual que el servidor: W.O./no-show/default valen walkover_loss.
    const lwo = WALKOVER_LOSS_STATUSES.has(match.status) ? 1 : 0;
    if (match.winner_participant_id === p1) {
      addStats(stats, p1, { w: 1 });
      addStats(stats, p2, { l: 1, lwo });
    } else if (match.winner_participant_id === p2) {
      addStats(stats, p2, { w: 1 });
      addStats(stats, p1, { l: 1, lwo });
    }
  }
  return stats;
}

function statsFromRows(rows: StandingsRow[]): Map<number, RankingStats> {
  const stats = new Map<number, RankingStats>();
  for (const row of rows) {
    stats.set(row.participant_id, {
      w: row.wins,
      l: row.losses,
      gg: row.games_won,
      gp: row.games_lost,
      dif: row.differential,
    });
  }
  return stats;
}

function buildSuggestedGroups(data: StandingsPayload, _statsByParticipant: Map<number, RankingStats>): RankingDraftGroup[] {
  // Ladder ranking is positional: the group's ladder order is the ranking.
  // W/L/GG/GP are displayed as context/tiebreaking evidence, but they must
  // not reorder the suggestion by themselves.
  return data.groups.map((group) => ({
    groupId: group.group_id,
    groupName: group.group_name,
    rows: [...group.standings].sort((a, b) => a.position - b.position || a.display_name.localeCompare(b.display_name, "es")),
  }));
}

function buildReadOnlyResultGroups(data: StandingsPayload): RankingDraftGroup[] {
  const activeGroups = data.groups.filter((group) => group.standings.length > 0);
  if (activeGroups.length > 0) {
    return activeGroups.map((group) => ({
      groupId: group.group_id,
      groupName: group.group_name,
      rows: [...group.standings].sort((a, b) => a.position - b.position),
    }));
  }
  return [{
    groupId: null,
    groupName: "Clasificación general",
    rows: [...data.ranking].sort((a, b) => a.position - b.position),
  }];
}

function buildPublishedRoundGroups(data: StandingsPayload, round: TournamentRound): RankingDraftGroup[] {
  const rowByParticipant = new Map<number, StandingsRow>();
  for (const row of data.ranking) rowByParticipant.set(row.participant_id, row);

  const grouped = new Map<number | null, { groupName: string; rows: Array<{ row: StandingsRow; position: number }> }>();
  for (const matchup of round.matchups) {
    const entries: Array<[number | null, number | null]> = [
      [matchup.player1_participant_id, matchup.player1_position],
      [matchup.player2_participant_id, matchup.player2_position],
    ];
    for (const [participantId, position] of entries) {
      if (!participantId || !position) continue;
      const row = rowByParticipant.get(participantId);
      if (!row) continue;
      const key = matchup.group_id ?? row.group_id ?? null;
      const current = grouped.get(key) ?? {
        groupName: matchup.group_name || row.group_name || "Sin grupo",
        rows: [],
      };
      if (!current.rows.some((item) => item.row.participant_id === participantId)) {
        current.rows.push({ row, position });
      }
      grouped.set(key, current);
    }
  }

  return Array.from(grouped.entries()).map(([groupId, value]) => ({
    groupId,
    groupName: value.groupName,
    rows: value.rows
      .sort((a, b) => a.position - b.position || a.row.display_name.localeCompare(b.row.display_name, "es"))
      .map((item) => item.row),
  }));
}

/**
 * Sin `?division` la pantalla junta los grupos de TODAS las categorías y el
 * payload de standings del admin solo trae `group_name` (contrato B1). Desde
 * 0096 el nombre es único por categoría, así que dos «Grupo 1» se
 * distinguen con su categoría: «Grupo 1 (Sub-10)». Solo cuenta los grupos
 * que la pantalla muestra; si el nombre no se repite queda tal cual.
 * Exportado para regresión.
 */
export function labelRankingGroupsByDivision(
  groups: RankingDraftGroup[],
  tournamentGroups: ReadonlyArray<Pick<TournamentGroup, "id" | "name" | "division_id" | "division">>,
): RankingDraftGroup[] {
  if (groups.length < 2 || tournamentGroups.length === 0) return groups;
  const shownIds = new Set(groups.map((group) => group.groupId).filter((groupId): groupId is number => groupId !== null));
  const shown = tournamentGroups.filter((group) => shownIds.has(group.id));
  const repeated = repeatedTournamentGroupNames(shown);
  if (repeated.size === 0) return groups;
  const byId = new Map(shown.map((group) => [group.id, group] as const));
  return groups.map((group) => {
    const source = group.groupId !== null ? byId.get(group.groupId) : undefined;
    if (!source) return group;
    const groupName = groupNameWithDivision(group.groupName, source.division?.name, repeated);
    return groupName === group.groupName ? group : { ...group, groupName };
  });
}

function globalRank(groups: RankingDraftGroup[], participantId: number): number | null {
  let rank = 1;
  for (const group of groups) {
    for (const row of group.rows) {
      if (row.participant_id === participantId) return rank;
      rank += 1;
    }
  }
  return null;
}

/** Datos de ranking por participante que la tabla de standings del admin
 * no trae: puntos capturados en el paso Ranking y si su categoría se
 * rankeó por puntos. */
export interface RankingPointsInfo {
  points: number | null;
  rankedByPoints: boolean;
}

/** Mapa participante → puntos de ranking, desde la lista de inscritos (la
 * respuesta de standings del admin tiene llaves fijas y no los trae). Una
 * categoría está «rankeada por puntos» si alguien en ella tiene puntos.
 * Exportado para regresión. */
export function rankingPointsByParticipant(
  participants: ReadonlyArray<{ id: number; division_id: number | null; ranking_points: number | null }>,
): Map<number, RankingPointsInfo> {
  const divisionsWithPoints = new Set<number | null>();
  for (const participant of participants) {
    if ((participant.ranking_points ?? 0) > 0) divisionsWithPoints.add(participant.division_id ?? null);
  }
  return new Map(
    participants.map((participant) => [
      participant.id,
      {
        points: participant.ranking_points,
        rankedByPoints: divisionsWithPoints.has(participant.division_id ?? null),
      },
    ] as const),
  );
}

function pointsHelpText(config: StandingsPointsConfig | null | undefined): string {
  if (!config) {
    return "Puntos de la liga: se ganan por partido y ordenan la tabla. Aparecen con los primeros resultados.";
  }
  return (
    `Puntos de la liga: ${config.win} por victoria, ${config.loss_completed} por partido terminado aunque se pierda` +
    ` y ${config.walkover_loss} por W.O. (no presentarse). Ordenan la tabla; aparecen con los primeros resultados.` +
    " En la sugerencia de una semana, igual que W y L, cuentan los partidos hasta la semana anterior."
  );
}

function AdminRankingGroup({
  group,
  groupIndex,
  groups,
  setGroups,
  statsByParticipant,
  readOnly = false,
  vocab,
  qualifiedIds,
  started,
  showPts,
  pointsConfig,
  rankingPoints,
  statsAreWeekFiltered = false,
}: {
  group: RankingDraftGroup;
  groupIndex: number;
  groups: RankingDraftGroup[];
  setGroups: (groups: RankingDraftGroup[]) => void;
  statsByParticipant: Map<number, RankingStats>;
  readOnly?: boolean;
  vocab: ScoreVocab;
  qualifiedIds: Set<number>;
  /** Eduardo 2026-10-01: sin resultados no hay lugares (colores), ni
   * clasificados (*), ni Rk Gral, ni movimientos. */
  started: boolean;
  /** Liga por puntos: columna Puntos. */
  showPts: boolean;
  pointsConfig?: StandingsPointsConfig | null;
  rankingPoints: Map<number, RankingPointsInfo>;
  /** W/L cuentan solo hasta la semana anterior: Puntos se calcula igual. */
  statsAreWeekFiltered?: boolean;
}) {
  const unitWord = vocab.ggHeader === "PG" ? "puntos" : "juegos (games)";
  const move = (rowIndex: number, direction: -1 | 1) => {
    const next = groups.map((g) => ({ ...g, rows: [...g.rows] }));
    const rows = next[groupIndex].rows;
    const target = rowIndex + direction;
    if (target < 0 || target >= rows.length) return;
    const [item] = rows.splice(rowIndex, 1);
    rows.splice(target, 0, item);
    setGroups(next);
  };

  return (
    <details className="card player-ranking-group is-player-group" open>
      <summary className="player-ranking-group__summary">
        <strong>{group.groupName}</strong>
        <span className="status-pill">{readOnly ? "Resultados" : `Nivel ${groupIndex + 1}`}</span>
        <span aria-hidden="true">▾</span>
      </summary>
      <TableScrollWrap wrapClassName="player-ranking-table-wrap" ariaLabel={`Ranking del grupo ${group.groupName}`}>
        <table className="player-ranking-table admin-ranking-editor-table">
          {/* Cada columna con su «?» (Eduardo 2026-10-01). */}
          <thead>
            <tr>
              <th className="col-player">
                <span className="hint-label">
                  Jugador
                  <FieldHint label="¿Qué muestra la columna Jugador?">
                    Lugar dentro del grupo y nombre. Los colores de 1.º, 2.º y 3.º lugar aparecen cuando la
                    categoría ya tiene resultados; antes es solo el orden inicial.
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  Rk Ini
                  <FieldHint label="¿Qué es Rk Ini?">
                    Ranking inicial: con qué llegó el jugador al torneo. Si la categoría se rankeó por puntos en el
                    paso Ranking, aquí van sus puntos; si no, su seed (de la siembra o del ranking oficial). En
                    escaleras es su lugar de salida dentro del nivel. S/R = sin ranking.
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  Rk Gral
                  <FieldHint label="¿Qué es Rk Gral?">
                    Ranking general: el lugar del jugador entre todos los de esta tabla, contando los grupos en
                    orden. Se calcula cuando la categoría ya tiene resultados; antes se ve «—».
                  </FieldHint>
                </span>
              </th>
              {showPts && (
                <th>
                  <span className="hint-label">
                    Puntos
                    <FieldHint label="¿Qué son los Puntos?">{pointsHelpText(pointsConfig)}</FieldHint>
                  </span>
                </th>
              )}
              <th>
                <span className="hint-label">
                  W
                  <FieldHint label="¿Qué es W?">
                    Victorias: partidos ganados. En la sugerencia de una semana cuentan los partidos hasta la semana anterior.
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  L
                  <FieldHint label="¿Qué es L?">Derrotas: partidos perdidos (incluye los perdidos por W.O.).</FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  {vocab.ggHeader}
                  <FieldHint label={`¿Qué es ${vocab.ggHeader}?`}>
                    {`${unitWord.charAt(0).toUpperCase()}${unitWord.slice(1)} ganados, sumando todos sus partidos.`}
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  {vocab.gpHeader}
                  <FieldHint label={`¿Qué es ${vocab.gpHeader}?`}>
                    {`${unitWord.charAt(0).toUpperCase()}${unitWord.slice(1)} perdidos, sumando todos sus partidos.`}
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  DIF
                  <FieldHint label="¿Qué es DIF?">
                    {`Diferencia: ${vocab.ggHeader} menos ${vocab.gpHeader}. Ej.: 36 ganados y 20 perdidos = +16. Positivo = ganó más ${unitWord} de los que perdió.`}
                  </FieldHint>
                </span>
              </th>
              <th>
                <span className="hint-label">
                  Forma
                  <FieldHint label="¿Qué es Forma?">
                    Los últimos 5 resultados, el más reciente a la derecha: W = ganó, L = perdió.
                  </FieldHint>
                </span>
              </th>
              {!readOnly && (
                <th className="col-adjust">
                  <span className="hint-label">
                    Ajuste
                    <FieldHint label="¿Qué es Ajuste?">
                      Flechas para subir o bajar a un jugador en la sugerencia antes de publicar el ranking de la
                      semana. No cambia ningún resultado.
                    </FieldHint>
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row, rowIndex) => {
              const stats = statsByParticipant.get(row.participant_id) ?? EMPTY_STATS;
              const rank = started ? globalRank(groups, row.participant_id) : null;
              // Sin resultados no hay lugares: nada de colores de podio.
              const podium = started && rowIndex < 3 ? rowIndex + 1 : null;
              const recentForm = (row.form ?? []).slice(-5);
              const pointsInfo = rankingPoints.get(row.participant_id);
              // S/R se decide con el seed real; en escalera el lugar de salida
              // en el nivel solo se muestra si el jugador traía seed.
              const realSeed = row.initial_rank ?? row.tournament_rank ?? null;
              const seed = realSeed != null ? (row.initial_group_rank ?? realSeed) : null;
              const pointsValue =
                statsAreWeekFiltered && pointsConfig ? leaguePointsFromStats(stats, pointsConfig) : row.points;
              return (
                <tr key={row.participant_id} className={podium ? "admin-ranking-row--podium" : undefined}>
                  <td className="col-player player-ranking-player" title={row.display_name}>
                    <span className="admin-ranking-player-cell">
                      <span
                        className={`admin-ranking-pos${podium ? ` admin-ranking-pos--${podium}` : ""}`}
                        aria-label={`Posición ${rowIndex + 1} del grupo`}
                      >
                        {rowIndex + 1}
                      </span>
                      <span className="admin-ranking-player-name">
                        {row.short_name || row.display_name}
                        {started && qualifiedIds.has(row.participant_id) && (
                          <span className="admin-ranking-qualified" title="Pasó a playoffs">*</span>
                        )}
                      </span>
                      {started ? <RankMovementBadge movement={row.rank_movement} delta={row.rank_delta} /> : null}
                    </span>
                  </td>
                  <td title={pointsInfo?.rankedByPoints && seed != null ? `Seed ${seed}` : undefined}>
                    {initialRankDisplay(seed, pointsInfo?.points, Boolean(pointsInfo?.rankedByPoints))}
                  </td>
                  <td>{rank ?? "—"}</td>
                  {showPts && <td>{started ? pointsValue : "—"}</td>}
                  <td>{stats.w}</td>
                  <td>{stats.l}</td>
                  <td>{stats.gg}</td>
                  <td>{stats.gp}</td>
                  <td className={stats.dif > 0 ? "admin-ranking-dif--up" : stats.dif < 0 ? "admin-ranking-dif--down" : undefined}>
                    {stats.dif > 0 ? `+${stats.dif}` : stats.dif}
                  </td>
                  <td>
                    {recentForm.length > 0 ? (
                      <span className="admin-ranking-form" aria-label={`Forma reciente: ${recentForm.join(", ")}`}>
                        {recentForm.map((result, formIndex) => (
                          <i
                            key={formIndex}
                            className={`admin-ranking-form__dot admin-ranking-form__dot--${result === "W" ? "w" : result === "L" ? "l" : "d"}`}
                          >
                            {result}
                          </i>
                        ))}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="col-adjust">
                      <div className="admin-ranking-actions">
                        <button type="button" className="btn btn--ghost btn--compact" onClick={() => move(rowIndex, -1)} disabled={rowIndex === 0}>↑</button>
                        <button type="button" className="btn btn--ghost btn--compact" onClick={() => move(rowIndex, 1)} disabled={rowIndex === group.rows.length - 1}>↓</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScrollWrap>
      {started && group.rows.some((row) => qualifiedIds.has(row.participant_id)) && (
        <p className="admin-ranking-qualified-note">
          * Pasó a playoffs.{" "}
          <FieldHint label="¿Qué significa Pasó a playoffs?">
            Con los resultados de hoy, el jugador queda entre los que pasan a la fase final según las reglas de
            clasificación del formato. Puede cambiar con los siguientes resultados; antes del primer resultado no se
            marca a nadie.
          </FieldHint>
        </p>
      )}
    </details>
  );
}

export default function AdminTournamentStandingsPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const [searchParams] = useSearchParams();
  const divisionFromQuery = searchParams.get("division");
  const initialDivisionId = divisionFromQuery && Number.isInteger(Number(divisionFromQuery))
    ? Number(divisionFromQuery)
    : null;
  const { token } = useAuth();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [data, setData] = useState<StandingsPayload | null>(null);
  const [qualificationPreview, setQualificationPreview] = useState<RoundRobinQualificationPreviewPayload | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [rounds, setRounds] = useState<TournamentRound[]>([]);
  // Grupos de todas las categorías: solo se cargan sin `?division`, para
  // etiquetar los «Grupo 1» repetidos (ver labelRankingGroupsByDivision).
  const [tournamentGroups, setTournamentGroups] = useState<TournamentGroup[]>([]);
  // Puntos del paso Ranking por participante (Rk Ini, Eduardo 2026-10-01).
  const [rankingPoints, setRankingPoints] = useState<Map<number, RankingPointsInfo>>(new Map());
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(initialDivisionId);
  const [selectedWeekKey, setSelectedWeekKey] = useState("");
  // La semana elegida a mano sobrevive las recargas: load() corre tras cada
  // acción y reponer el default pisaba el click del admin (QA Cowork #12).
  const userSelectedWeekRef = useRef(false);
  const [draftGroups, setDraftGroups] = useState<RankingDraftGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const loadSeqRef = useRef(0);
  const activeDivision = selectedDivisionId
    ? divisions.find((division) => division.id === selectedDivisionId) ?? null
    : null;
  const phaseConfig = activeRoundRobinPhaseConfig(null, activeDivision);
  const isRoundRobinPlayoffs = isRoundRobinPlayoffPhase(phaseConfig) || qualificationPreview?.enabled === true;
  const isElimination = tournament?.tournament_type === "elimination";
  const vocab = scoreVocab(tournament?.sport);
  // Eduardo 2026-10-01: «aquí todavía no empieza el torneo». Sin ningún
  // resultado en la categoría no hay lugares, clasificados ni Rk Gral.
  const pageHasResults = standingsHaveResults(data);
  // Pedido Eduardo 2026-08-27: marcar con * a los que pasaron a playoffs,
  // en la tabla y en el PDF, con la leyenda al pie.
  const qualifiedParticipantIds = useMemo(
    () =>
      new Set(
        (qualificationPreview?.enabled ? qualificationPreview.qualifiers : []).map(
          (qualifier) => qualifier.participant_id,
        ),
      ),
    [qualificationPreview],
  );

  useEffect(() => {
    setSelectedDivisionId((current) => (current === initialDivisionId ? current : initialDivisionId));
    // Cambiar de categoría sí regresa al default de semana.
    userSelectedWeekRef.current = false;
  }, [initialDivisionId]);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const requestSeq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const divisionParams = initialDivisionId ? { division_id: initialDivisionId } : {};
      const [tournamentResp, standingsResp, resultsResp, roundsResp, divisionsResp, qualificationResp, groupsResp, participantsResp] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.standings.get(token, tournamentId, divisionParams),
        api.resultReports.listTournamentResultsForAdmin(token, tournamentId),
        api.rounds.list(token, tournamentId, divisionParams),
        api.tournaments.listDivisions(token, tournamentId),
        api.standings.qualificationPreview(token, tournamentId, divisionParams),
        initialDivisionId
          ? Promise.resolve(null)
          : api.groups.list(token, tournamentId).catch(() => null),
        // Solo para Rk Ini: si falla, la tabla se ve igual (seed o S/R).
        api.participants.list(token, tournamentId, divisionParams).catch(() => null),
      ]);
      if (requestSeq !== loadSeqRef.current) return;
      const filteredMatches = initialDivisionId
        ? resultsResp.data.matches.filter((match) => match.division_id === initialDivisionId)
        : resultsResp.data.matches;
      setTournament(tournamentResp.data.tournament);
      setDivisions(divisionsResp.data.divisions);
      setData(standingsResp.data);
      setQualificationPreview(qualificationResp.data);
      setMatches(filteredMatches);
      setRounds(roundsResp.data.rounds);
      setTournamentGroups(groupsResp?.data.groups ?? []);
      setRankingPoints(rankingPointsByParticipant(participantsResp?.data.participants ?? []));
      if (!userSelectedWeekRef.current) {
        setSelectedWeekKey(defaultWeekKey(filteredMatches, roundsResp.data.rounds));
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (requestSeq === loadSeqRef.current) setLoading(false);
    }
  }, [token, tournamentId, initialDivisionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const weekOptions = useMemo(() => makeWeekOptions(matches, rounds), [matches, rounds]);
  // Fail-safe con aliases: la clave seleccionada puede ser cualquiera de los
  // raws del bucket (defaultWeekKey devuelve labels de matches).
  const selectedWeek = findWeekOption(weekOptions, selectedWeekKey) ?? weekOptions[0];
  // El <select> y los chips comparan contra option.key: si la clave llegó
  // como alias, se normaliza a la clave canónica de la opción resuelta.
  const selectedWeekOptionKey = selectedWeek?.key ?? selectedWeekKey;
  const selectedRound = useMemo(
    () => rounds.find((round) => (round.name || `Jornada ${round.round_number}`) === selectedWeekKey || round.round_number === selectedWeek?.sort),
    [rounds, selectedWeek?.sort, selectedWeekKey],
  );
  const selectedRankingIsPublished = !isElimination && selectedRound?.status === "published";
  const legacyStatsWeekKey = useMemo(() => latestLegacyStatsWeek(data?.legacy_stats), [data?.legacy_stats]);
  const statsByParticipant = useMemo(() => {
    if (isElimination && data) {
      return statsFromRows(data.ranking);
    }
    // Legacy-imported ladders expose the latest current ladder order in this
    // table. Use the latest authoritative legacy W/L/GG/GP snapshot too; using
    // the selected/publication week can show Semana 13 stats beside Semana 14
    // positions (Marcos #1 / Eduardo #2 but Eduardo still 10-1).
    const legacy = data?.legacy_stats;
    if (legacy?.available) {
      const weekStats = legacy.by_week?.[legacyStatsWeekKey ?? ""];
      if (weekStats) {
        const map = new Map<number, RankingStats>();
        for (const [pidStr, vals] of Object.entries(weekStats)) {
          const pid = Number(pidStr);
          if (!Number.isNaN(pid)) {
            map.set(pid, {
              w: vals.w ?? 0,
              l: vals.l ?? 0,
              gg: vals.gg ?? 0,
              gp: vals.gp ?? 0,
              dif: (vals.gg ?? 0) - (vals.gp ?? 0),
            });
          }
        }
        return map;
      }
    }
    // Fallback to computed stats
    return cumulativeStatsUntil(matches, selectedWeek?.sort ?? Number.MAX_SAFE_INTEGER, tournament?.sport);
  }, [data, data?.legacy_stats, isElimination, legacyStatsWeekKey, matches, selectedWeek?.sort, tournament?.sport]);

  const selectedWeekUsesLegacyStats = Boolean(
    data?.legacy_stats?.available &&
      legacyStatsWeekKey &&
      data.legacy_stats.by_week?.[legacyStatsWeekKey],
  );

  const displayGroups = useMemo(() => {
    if (isElimination && data) return buildReadOnlyResultGroups(data);
    return draftGroups;
  }, [data, draftGroups, isElimination]);
  // Solo para mostrar e imprimir: el borrador conserva el nombre original.
  const labeledGroups = useMemo(
    () => labelRankingGroupsByDivision(displayGroups, tournamentGroups),
    [displayGroups, tournamentGroups],
  );
  const payloadGroupsById = useMemo(
    () => new Map((data?.groups ?? []).map((group) => [group.group_id, group] as const)),
    [data?.groups],
  );
  // Un grupo «empezó» cuando la página tiene resultados y alguno de sus
  // jugadores ya jugó (sin filtro de categoría, otras pueden ir adelante).
  const groupStarted = useCallback(
    (group: RankingDraftGroup) =>
      pageHasResults &&
      (Boolean(data?.legacy_stats?.available) ||
        rowsHaveResults(group.rows) ||
        group.rows.some((row) => {
          const stats = statsByParticipant.get(row.participant_id);
          return Boolean(stats && stats.w + stats.l > 0);
        })),
    [data?.legacy_stats?.available, pageHasResults, statsByParticipant],
  );

  const regenerateSuggestion = useCallback(() => {
    if (!data || isElimination) return;
    if (selectedRankingIsPublished && selectedRound) {
      setDraftGroups(buildPublishedRoundGroups(data, selectedRound));
      return;
    }
    setDraftGroups(buildSuggestedGroups(data, statsByParticipant));
  }, [data, isElimination, selectedRankingIsPublished, selectedRound, statsByParticipant]);

  useEffect(() => {
    regenerateSuggestion();
  }, [regenerateSuggestion]);

  async function handlePublishRanking() {
    if (!token || !data) return;
    setPublishing(true);
    setError(null);
    setMessage(null);
    try {
      const updates = draftGroups.flatMap((group) => group.rows).map((row) => ({
        participantId: row.participant_id,
        currentRank: globalRank(draftGroups, row.participant_id),
      }));
      for (const update of updates) {
        if (!update.currentRank) continue;
        await api.participants.update(token, tournamentId, update.participantId, {
          current_rank: update.currentRank,
        });
      }
      const refreshed = await api.standings.get(token, tournamentId, selectedDivisionId ? { division_id: selectedDivisionId } : {});
      setData(refreshed.data);
      setMessage(`Ranking publicado para ${selectedWeek ? weekOptionDisplay(selectedWeek) : "la semana actual"}. Ahora puedes generar sus matchups.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo publicar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setPublishing(false);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  function printRankingPdf() {
    if (displayGroups.length === 0) {
      setError("Todavía no hay tabla para imprimir.");
      return;
    }
    // Con el preset ITF (sin liga por puntos) la columna Pts no existe:
    // la tabla oficial ordena y muestra por victorias. La bandera se lee POR
    // GRUPO (en torneos con reglas mixtas por categoría la bandera root es
    // ambigua); su ausencia = comportamiento histórico (Pts visible).
    const sections = labeledGroups
      .map((group) => {
        const showPts = showPointsColumn(
          payloadGroupsById.get(group.groupId) ?? data,
        );
        // Antes del primer resultado no se marca a nadie como clasificado.
        const started = groupStarted(group);
        const groupHasQualified = started && group.rows.some((row) =>
          qualifiedParticipantIds.has(row.participant_id),
        );
        return `
      <h2 class="print-section-title">${escapeHtml(group.groupName)}</h2>
      <table>
        <thead><tr><th>Pos</th><th>Jugador</th><th>JJ</th><th>G</th><th>P</th><th>${vocab.setsHeader}</th><th>${vocab.ggHeader}</th><th>${vocab.gpHeader}</th><th>DIF</th>${showPts ? "<th>Pts</th>" : ""}</tr></thead>
        <tbody>${group.rows
          .map(
            (row, index) => `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(row.short_name || row.display_name)}${started && qualifiedParticipantIds.has(row.participant_id) ? " *" : ""}</td>
          <td>${row.matches_played}</td>
          <td>${row.wins}</td>
          <td>${row.losses}</td>
          <td>${row.sets_won}-${row.sets_lost}</td>
          <td>${row.games_won}</td>
          <td>${row.games_lost}</td>
          <td>${row.differential > 0 ? "+" : ""}${row.differential}</td>
          ${showPts ? `<td>${started ? row.points : "—"}</td>` : ""}
        </tr>`,
          )
          .join("")}</tbody>
      </table>${groupHasQualified ? '<p style="margin:4px 0 0;font-size:11px;opacity:0.75;">* Pasó a playoffs.</p>' : ""}`;
      })
      .join("");
    try {
      openPrintWindow({
        title: `${isElimination ? "Clasificación" : "Ranking"} · ${tournament?.name ?? "Torneos123"}`,
        eyebrow: isElimination ? "Clasificación oficial" : "Ranking oficial",
        heading: tournament?.name ?? "Torneos123",
        subtitle: activeDivision?.name,
        bodyHtml: sections,
        // Logo del organizador arriba a la derecha (Eduardo 2026-10-01).
        organizerSlot: true,
        organizerLogoUrl: uploadAssetHref(tournament?.organizer_logo_url),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la impresión.");
    }
  }

  return (
    <>
      <section className="player-ranking-hero admin-ranking-hero">
        <div>
          <span className="eyebrow">{tournament?.name ?? "Torneo"}</span>
          <h1>{isElimination ? "Clasificación" : "Ranking"}</h1>
          <button type="button" className="btn btn--compact btn--ghost" onClick={printRankingPdf}>
            🖨 Imprimir / PDF
          </button>
          <p>
            {isElimination
              ? "Vista de resultados reales del bracket: victorias, derrotas, games y diferencial calculados desde los partidos capturados. En eliminatoria no se publica ni edita un ranking semanal."
              : "Flujo: resultados publicados → ranking editable/publicado → Enfrentamientos sugeridos con ese ranking. Si los enfrentamientos de una semana ya están publicados, ese ranking ya queda publicado para esa semana."}
          </p>
        </div>
        {!isElimination && (
          <div className="player-games-round-filter player-ranking-week-filter">
          <span>{selectedRankingIsPublished ? "Semana publicada" : "Semana a publicar"}</span>
          <select
            value={selectedWeekOptionKey}
            onChange={(event) => {
              userSelectedWeekRef.current = true;
              setSelectedWeekKey(event.target.value);
            }}
            disabled={weekOptions.length === 0}
            aria-label="Semana a publicar"
          >
            {weekOptions.map((option) => (
              <option key={option.key} value={option.key}>{weekOptionDisplay(option)}</option>
            ))}
          </select>
          <div className="ranking-week-chip-list" role="listbox" aria-label="Seleccionar semana">
            {weekOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`ranking-week-chip${option.key === selectedWeekOptionKey ? " is-active" : ""}`}
                onClick={() => {
                  userSelectedWeekRef.current = true;
                  setSelectedWeekKey(option.key);
                }}
                role="option"
                aria-selected={option.key === selectedWeekOptionKey}
              >
                {weekOptionDisplay(option)}
              </button>
            ))}
          </div>
          {selectedWeek && (
            <small>
              {selectedRankingIsPublished
                ? `Ranking publicado por ${weekOptionDisplay(selectedWeek)}`
                : `Sugerencia para ${weekOptionDisplay(selectedWeek)}`}
            </small>
          )}
          </div>
        )}
      </section>

      <section className="card player-ranking-legend">
        {isElimination ? (
          <>
            <span><strong>Pos</strong> clasificación por resultados del bracket</span>
            <span><strong>W/L</strong> victorias / derrotas capturadas</span>
            <span><strong>{vocab.ggHeader}/{vocab.gpHeader}/DIF</strong> {vocab.ggGpLegend}</span>
            <span><strong>Sin edición</strong> la eliminatoria se ordena por resultados reales</span>
          </>
        ) : (
          <>
            <span><strong>Rk Ini</strong> ranking inicial (puntos, seed o lugar de salida en escalera; S/R = sin ranking)</span>
            <span><strong>Rk Gral</strong> ranking vigente a publicar, con los primeros resultados</span>
            <span><strong>W/L</strong> victorias / derrotas acumuladas hasta la semana anterior cuando es sugerencia</span>
            <span><strong>Publicar</strong> actualiza el rank actual antes de generar enfrentamientos</span>
            <span><strong>?</strong> toca el signo junto a cada columna para ver qué significa</span>
          </>
        )}
      </section>

      {isRoundRobinPlayoffs && (
        <section className="card standings-phase-bridge" aria-label="Conexión grupos playoffs ranking resultados calendario comunicación">
          <div>
            <span className="eyebrow">Fase final</span>
            <h2>Ranking conectado a clasificación y playoffs</h2>
            <p>
              {roundRobinPhaseSummary(phaseConfig) || "La tabla por grupos define los clasificados; después los partidos de playoffs viven como partidos reales en Resultados y Calendario."}
            </p>
          </div>
          <div className="standings-phase-bridge__grid">
            <span><strong>Fase de grupos</strong>{groupScheduleTypeLabel(phaseConfig)}</span>
            <span><strong>Clasificación</strong>{qualificationRulesSummary(phaseConfig)}</span>
            <span>
              <strong>Clasificados actuales</strong>
              {pageHasResults ? qualificationPreview?.summary.qualifier_count ?? 0 : "Se definen con los primeros resultados"}
            </span>
          </div>
          {pageHasResults && qualificationPreview?.enabled && qualificationPreview.qualifiers.length > 0 && (
            <div className="standings-qualifier-strip">
              {qualificationPreview.qualifiers.slice(0, 10).map((row) => (
                <span key={row.participant_id} title={row.qualification_reason}>
                  #{row.seed} {row.display_name} · {row.qualification_reason}
                </span>
              ))}
              {qualificationPreview.qualifiers.length > 10 && <span>+{qualificationPreview.qualifiers.length - 10} más</span>}
            </div>
          )}
        </section>
      )}

      {error && <p role="alert" className="form-error">{error}</p>}
      {message && <p className="form-message">{message}</p>}
      {!isElimination && selectedRankingIsPublished && selectedWeek && (
        <p className="form-message" data-no-toast>Ranking {weekOptionDisplay(selectedWeek)} ya publicado: viene de sus enfrentamientos publicados.</p>
      )}
      {selectedWeekUsesLegacyStats && selectedWeek && legacyStatsWeekKey && (
        <p className="form-message standings-legacy-source-badge">
          Datos legacy activos: W/L/{vocab.ggHeader}/{vocab.gpHeader} calculados hasta Semana {legacyStatsWeekKey} para el ranking vigente.
        </p>
      )}

      <section className="card">
        <div className="standings-summary">
          <div className="standings-stat">
            <span className="standings-stat__label">Jugadores</span>
            <strong className="standings-stat__value">{data?.summary.total_participants ?? "—"}</strong>
          </div>
          <div className="standings-stat">
            <span className="standings-stat__label">Resultados</span>
            <strong className="standings-stat__value">{data?.summary.matches_with_results ?? "—"}</strong>
          </div>
          <div className="standings-stat">
            <span className="standings-stat__label">Grupos</span>
            <strong className="standings-stat__value">{data?.summary.groups_count ?? "—"}</strong>
          </div>
          {!isElimination && (
            <div className="form__actions" style={{ marginLeft: "auto" }}>
              <button type="button" className="btn btn--ghost" onClick={regenerateSuggestion} disabled={!data || publishing}>
                {selectedRankingIsPublished ? "Releer publicado" : "Recrear sugerencia"}
              </button>
              <button type="button" className="btn" onClick={() => void handlePublishRanking()} disabled={!data || publishing || draftGroups.length === 0 || selectedRankingIsPublished}>
                {publishing
                  ? "Publicando…"
                  : selectedRankingIsPublished
                    ? "Ranking publicado"
                    : selectedWeek
                      ? `Publicar ranking ${weekOptionDisplay(selectedWeek)}`
                      : "Publicar ranking actual"}
              </button>
            </div>
          )}
        </div>
      </section>

      {!loading && data && !pageHasResults && displayGroups.length > 0 && (
        <p className="form-message" data-no-toast>
          Todavía no hay resultados en {activeDivision ? "esta categoría" : "el torneo"}: la tabla muestra el orden
          inicial. Los lugares, el Rk Gral y quién pasa a playoffs aparecen con el primer resultado.
        </p>
      )}
      {loading ? (
        <section className="card"><p className="muted">Cargando ranking…</p></section>
      ) : displayGroups.length === 0 ? (
        <section className="card"><p className="muted">Sin datos de ranking.</p></section>
      ) : (
        <section className="player-ranking-grid admin-ranking-editor" aria-label={isElimination ? "Clasificación por resultados" : "Ranking editable por grupo"}>
          {labeledGroups.map((group, idx) => (
            <AdminRankingGroup
              key={String(group.groupId ?? "none")}
              group={group}
              groupIndex={idx}
              groups={displayGroups}
              setGroups={setDraftGroups}
              statsByParticipant={statsByParticipant}
              readOnly={isElimination}
              vocab={vocab}
              qualifiedIds={qualifiedParticipantIds}
              started={groupStarted(group)}
              showPts={showPointsColumn(payloadGroupsById.get(group.groupId) ?? data)}
              pointsConfig={data?.points_config}
              rankingPoints={rankingPoints}
              statsAreWeekFiltered={!isElimination && !selectedWeekUsesLegacyStats}
            />
          ))}
        </section>
      )}

      <TournamentBottomNav tournamentId={tournamentId} />
    </>
  );
}
